// Import the enums from their source modules, NOT via the webSocketService
// handlers that re-export them. This file reads enum members at
// module-evaluation time (the tripStatusListToEventMap object literal below),
// so importing through the handlers put those reads inside an import cycle
// (updateTripLeg -> tripleg.slice -> this file -> updateTripLeg) where the
// binding could still be uninitialized — a crash that only lucky
// module-initialization order avoided.
import { ReservationEventType } from '../events/reservation';
import { TripLegEventType } from '../events/trip-leg';

export interface TripLegStatusItem {
  description: string;
  passengerUUID: string;
  tripLegReferenceID: string;
  status: string;
  tripLegStatusReasonBCs: string;
  modifiedByUserID: string;
  modifiedDateTimeUTC: string;
}

export enum TripLegStatus {
  Draft = 'draft',
  OnHold = 'onhold',
  Open = 'open',
  Offered = 'offered',
  Accepted = 'accepted',
  Confirmed = 'confirmed',
  Onsite = 'onsite',
  Onboard = 'onboard',
  Canceled = 'canceled',
  NoLoad = 'noload',
  Completed = 'completed',
  Denied = 'denied',
  Abandoned = 'abandoned',
  Void = 'void',
  InTransit = 'driverintransit',
  CanceledLate = 'canceledlate',
  Dropoff = 'dropoff',
  Rejected = 'rejected',
}

export enum TripLegStatusBC {
  Draft = 'DRAFT',
  OnHold = 'ONHOLD',
  Open = 'OPEN',
  Offered = 'OFFERED',
  Accepted = 'ACCEPTED',
  Confirmed = 'CONFIRMED',
  Onsite = 'ONSITE',
  Onboard = 'ONBOARD',
  Canceled = 'CANCELED',
  NoLoad = 'NOLOAD',
  Completed = 'COMPLETED',
  Denied = 'DENIED',
  Abandoned = 'ABANDONED',
  Void = 'VOID',
  InTransit = 'DRIVERINTRANSIT',
  CanceledLate = 'CANCELEDLATE',
  Dropoff = 'DROPOFF',
  Rejected = 'REJECTED',
  NoPassengerForDriver = 'NOPASSENGERFORDRIVER',
  LateDoctorReschedule = 'LATEDOCRESCHEDULE',
}

export const TripLegStatusToBC: Record<TripLegStatus, TripLegStatusBC> = {
  [TripLegStatus.Draft]: TripLegStatusBC.Draft,
  [TripLegStatus.OnHold]: TripLegStatusBC.OnHold,
  [TripLegStatus.Open]: TripLegStatusBC.Open,
  [TripLegStatus.Offered]: TripLegStatusBC.Offered,
  [TripLegStatus.Accepted]: TripLegStatusBC.Accepted,
  [TripLegStatus.Confirmed]: TripLegStatusBC.Confirmed,
  [TripLegStatus.Onsite]: TripLegStatusBC.Onsite,
  [TripLegStatus.Onboard]: TripLegStatusBC.Onboard,
  [TripLegStatus.Canceled]: TripLegStatusBC.Canceled,
  [TripLegStatus.NoLoad]: TripLegStatusBC.NoLoad,
  [TripLegStatus.Completed]: TripLegStatusBC.Completed,
  [TripLegStatus.Denied]: TripLegStatusBC.Denied,
  [TripLegStatus.Abandoned]: TripLegStatusBC.Abandoned,
  [TripLegStatus.Void]: TripLegStatusBC.Void,
  [TripLegStatus.InTransit]: TripLegStatusBC.InTransit,
  [TripLegStatus.CanceledLate]: TripLegStatusBC.CanceledLate,
  [TripLegStatus.Dropoff]: TripLegStatusBC.Dropoff,
  [TripLegStatus.Rejected]: TripLegStatusBC.Rejected,
};

// Converts a websocket event's lowercase `status` (e.g. 'driverintransit')
// into the `tripLegStatusBC` that search results and tables use
// (e.g. 'DRIVERINTRANSIT').
export const toTripLegStatusBC = (status: string): string =>
  TripLegStatusToBC[status.toLowerCase() as TripLegStatus] ??
  status.toUpperCase();

export const tripStatusUnallocatedList = [TripLegStatus.Open];

export const tripStatusAllocatedList = [
  TripLegStatus.Open,
  TripLegStatus.Offered,
  TripLegStatus.Accepted,
];

export const tripStatusInProgressList = [
  TripLegStatus.InTransit,
  TripLegStatus.Onsite,
  TripLegStatus.Onboard,
  TripLegStatus.Confirmed,
];

export const tripStatusWithEtaList = [
  TripLegStatus.InTransit,
  TripLegStatus.Onsite,
  TripLegStatus.Onboard,
];

export const tripStatusUnattestedList = [
  TripLegStatus.Completed,
  TripLegStatus.NoLoad,
  TripLegStatus.CanceledLate,
];

export const tripStatusInactiveList = [
  TripLegStatus.Canceled,
  TripLegStatus.CanceledLate,
  TripLegStatus.Completed,
  TripLegStatus.NoLoad,
];

export const TripStatusTodaysTripsBCList = [
  TripLegStatus.Open.toUpperCase(),
  // The ticket says only OPEN trips but that doesn't really make sense since
  // the table shows assigned provider and status (which will always be open).
  // I think the following two statuses also make sense but we'll have to get
  // approval for that.
  // TripLegStatus.Offered.toUpperCase(),
  // TripLegStatus.Accepted.toUpperCase(),
];

export const tripStatusListToEventMap = {
  tripStatusUnallocatedList: [
    ReservationEventType.ReservationActivated,
    TripLegEventType.OpenTripLegUpdated,
  ],
  tripStatusAllocatedList: [
    ReservationEventType.ReservationActivated,
    TripLegEventType.TripLegOffered,
    TripLegEventType.TripLegManuallyAllocated,
    TripLegEventType.TripLegAccepted,
    TripLegEventType.OpenTripLegUpdated,
    TripLegEventType.OfferedTripLegUpdated,
    TripLegEventType.AcceptedTripLegUpdated,
  ],
  tripStatusInProgressList: [
    TripLegEventType.TripLegDriverInTransit,
    TripLegEventType.TripLegArrived,
    TripLegEventType.TripLegBoarded,
    TripLegEventType.TripLegConfirmed,
  ],
  tripStatusWithEtaList: [
    TripLegEventType.TripLegDriverInTransit,
    TripLegEventType.TripLegArrived,
    TripLegEventType.TripLegBoarded,
  ],
  tripStatusUnattestedList: [
    TripLegEventType.TripLegCompleted,
    TripLegEventType.TripLegNotLoaded,
    TripLegEventType.TripLegCanceledLate,
  ],
  tripStatusInactiveList: [
    TripLegEventType.TripLegCanceled,
    TripLegEventType.TripLegCanceledLate,
    TripLegEventType.TripLegCompleted,
    TripLegEventType.TripLegNotLoaded,
  ],
};
